/**
 * Backup, restore and emergency reset for the local database.
 *
 * The whole operational state of an installation is IndexedDB, and IndexedDB
 * lives inside one browser profile. This module is therefore the only way the
 * data ever leaves a device: `exportDatabase()` reads every operational table
 * into one plain JSON object, `importDatabase()` puts it back as a whole, and
 * `resetToSeedData()` returns an installation to the state it shipped in.
 *
 * Two tables are deliberately *not* exported: `pins` and `sessions`. A PIN
 * digest and an open session are credentials bound to one physical device, and
 * a backup file is something people mail to themselves — restoring one must
 * never hand somebody else's device a way in, and must never lock the operator
 * out of the device they are restoring on.
 *
 * Reads need `EXPORT_DATA`; overwriting or wiping the database is a system
 * action and needs `MANAGE_SYSTEM_SETTINGS`, which is reserved to a super-admin
 * by the permissions engine.
 */
import { db, DB_NAME, DB_VERSION } from './index';
import { resetAuditData } from './audit-store';
import { resetExternalPeople } from './external-people-store';
import { resetFleetData, seedSoldierRoster, seedVehicleRoster } from './fleet-store';
import { resetTaskData, seedTaskData } from './task-store';
import { APP_CONFIG } from '../core/config';
import { assertPermission } from '../core/permissions';
import { toDateKey } from '../core/task-filters';
import type { ISODateString } from '../types/index';
import type { User } from '../types/user';

/** Marker written into every file, so a foreign JSON is refused on sight. */
export const BACKUP_FORMAT = 'shinua99-tasks-backup';

/** Bumped only when the file shape itself changes. */
export const BACKUP_VERSION = 1;

/**
 * The tables a backup carries. `pins` and `sessions` are device credentials and
 * are never written to a file — see the module note above.
 */
export const BACKUP_TABLES = [
  'users',
  'devices',
  'vehicles',
  'soldiers',
  'tasks',
  'taskTypes',
  'contacts',
  'taskTeams',
  'taskCrew',
  'auditLogs',
  'taskProgress',
  'timelineEvents',
  'quickDriverTasks',
  // אנשי חוץ. Unit data like any other — the people a manager kept so the same
  // name is not typed twice — so a backup carries them and a restore puts them
  // back in their own table, never in the מצבה.
  'externalPeople',
] as const;

export type BackupTableName = (typeof BACKUP_TABLES)[number];

/** Tables that exist in the database but are intentionally left out. */
export const EXCLUDED_TABLES: readonly string[] = ['pins', 'sessions'];

export interface BackupFile {
  format: typeof BACKUP_FORMAT;
  version: number;
  appId: string;
  database: { name: string; version: number };
  exportedAt: ISODateString;
  /** The `User.id` that produced the file. */
  exportedBy?: string;
  /** Row count per table, so a file can be read without parsing every row. */
  counts: Record<string, number>;
  tables: Record<string, unknown[]>;
}

/** `shinua99_backup_2026-08-02.json` — the mandated file name. */
export function backupFileName(now: number = Date.now()): string {
  return `shinua99_backup_${toDateKey(now)}.json`;
}

/** How many rows each table currently holds — the settings screen's counters. */
export async function databaseCounts(): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const name of BACKUP_TABLES) {
    counts[name] = await db.table(name).count();
  }
  return counts;
}

// ── Export ───────────────────────────────────────────────────────────

/** Reads every exported table into one JSON-serialisable object. */
export async function exportDatabase(
  actor: User,
  now: number = Date.now(),
): Promise<BackupFile> {
  assertPermission(actor, 'EXPORT_DATA');

  const tables: Record<string, unknown[]> = {};
  const counts: Record<string, number> = {};
  for (const name of BACKUP_TABLES) {
    const rows = await db.table(name).toArray();
    tables[name] = rows;
    counts[name] = rows.length;
  }

  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    appId: APP_CONFIG.appId,
    database: { name: DB_NAME, version: DB_VERSION },
    exportedAt: new Date(now).toISOString(),
    exportedBy: actor.id,
    counts,
    tables,
  };
}

/** The file's text, indented so it stays readable in an editor. */
export function serializeBackup(backup: BackupFile): string {
  return JSON.stringify(backup, null, 2);
}

// ── Import ───────────────────────────────────────────────────────────

/** Raised by {@link parseBackup} — carries a Hebrew sentence for the screen. */
export class BackupFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BackupFormatError';
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Turns file text into a backup, refusing anything that is not one. Nothing is
 * written to the database until this has passed.
 */
export function parseBackup(text: string): BackupFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new BackupFormatError('הקובץ אינו קובץ JSON תקין');
  }

  if (!isPlainObject(parsed)) throw new BackupFormatError('מבנה הקובץ אינו תקין');
  if (parsed.format !== BACKUP_FORMAT) {
    throw new BackupFormatError('הקובץ אינו גיבוי של משימות שינוע 99');
  }
  const version = Number(parsed.version);
  if (!Number.isFinite(version) || version < 1) {
    throw new BackupFormatError('לקובץ הגיבוי אין מספר גרסה תקין');
  }
  if (version > BACKUP_VERSION) {
    throw new BackupFormatError(`גרסת הגיבוי (${version}) חדשה מגרסת המערכת`);
  }
  if (!isPlainObject(parsed.tables)) {
    throw new BackupFormatError('בקובץ הגיבוי אין טבלאות');
  }
  for (const [name, rows] of Object.entries(parsed.tables)) {
    if (!Array.isArray(rows)) throw new BackupFormatError(`הטבלה "${name}" אינה רשימת רשומות`);
  }

  return parsed as unknown as BackupFile;
}

export interface ImportSummary {
  /** Rows written, per table. */
  restored: Record<string, number>;
  /** Table names in the file this version does not know — left untouched. */
  skipped: string[];
  /** Total rows written. */
  total: number;
}

/**
 * Replaces the database with the contents of a backup, table by table, inside a
 * single transaction — a restore either lands whole or changes nothing.
 *
 * A table missing from the file is cleared, because the file is the new state
 * of the installation rather than a patch on top of it. The one exception is
 * the acting account: if the file's `users` table does not carry it, the row is
 * written back afterwards, so restoring a colleague's backup can never lock the
 * operator out of the device in their hand.
 */
export async function importDatabase(actor: User, backup: BackupFile): Promise<ImportSummary> {
  assertPermission(actor, 'MANAGE_SYSTEM_SETTINGS');

  const known = new Set<string>(BACKUP_TABLES);
  const skipped = Object.keys(backup.tables ?? {}).filter((name) => !known.has(name));
  const restored: Record<string, number> = {};

  await db.transaction(
    'rw',
    BACKUP_TABLES.map((name) => db.table(name)),
    async () => {
      for (const name of BACKUP_TABLES) {
        const rows = Array.isArray(backup.tables?.[name]) ? backup.tables[name] : [];
        await db.table(name).clear();
        if (rows.length > 0) await db.table(name).bulkPut(rows);
        restored[name] = rows.length;
      }

      // The operator's own account survives every restore.
      if (!(await db.users.get(actor.id))) {
        await db.users.put({ ...actor });
        restored.users += 1;
      }
    },
  );

  return {
    restored,
    skipped,
    total: Object.values(restored).reduce((sum, count) => sum + count, 0),
  };
}

/** Reads a file's text and restores it in one step. */
export async function restoreFromText(actor: User, text: string): Promise<ImportSummary> {
  return importDatabase(actor, parseBackup(text));
}

// ── Emergency reset ──────────────────────────────────────────────────

/**
 * Wipes the operational data and puts the installation back to the state it
 * shipped in: an empty board, an empty ledger, the shipped task-type catalogue,
 * the company's own roster and its own twenty-eight vehicles, each back at the
 * location the handover sheet recorded for it. Nothing is re-seeded with sample
 * records — a demo board and a demo fleet do not exist.
 *
 * Accounts, devices and the operator's session are left alone: this is a data
 * reset, not a factory reset of the installation's identity.
 */
export async function resetToSeedData(actor: User): Promise<void> {
  assertPermission(actor, 'MANAGE_SYSTEM_SETTINGS');
  await resetTaskData();
  await resetFleetData();
  await resetAuditData();
  // The directory is operational data too — a reset installation holds no
  // contractors, exactly as it holds no tasks.
  await resetExternalPeople();
  await seedTaskData();
  await seedSoldierRoster();
  await seedVehicleRoster();
}
